/* eslint-disable no-lonely-if */
/* eslint-disable no-nested-ternary */
import { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import { useShallow } from 'zustand/shallow';
import Center from '../../assets/styles/Center';
import FormContainer from '../../assets/styles/FormContainer';
import Text from '../../assets/styles/Text';
import Error from '../../components/Error';
import Loader from '../../components/Loader';
import { useUpdateModelDescription } from '../../hooks/mutations/useModelMutations';
import { useCities } from '../../hooks/queries/useCities';
import {
  useFiltersByGender,
  useModelFilters,
} from '../../hooks/queries/useFilters';
import { useModelServices, useServices } from '../../hooks/queries/useServices';
import { useModelById } from '../../hooks/queries/useUsers';
import { useStore } from '../../stores';
import BasicInfoSection from './components/BasicInfoSection';
import FiltersSection from './components/FiltersSection';
import ServicesSection from './components/ServicesSection';
import * as styles from './styles';

export default function ModelDescription() {
  const [selectedFilters, setSelectedFilters] = useState({});
  const [selectedServices, setSelectedServices] = useState({});
  const [formData, setFormData] = useState({
    nameFantasy: '',
    impactPhrase: '',
    description: '',
    selectedCity: '',
    district: '',
  });

  const navigate = useNavigate();

  const { user } = useStore(
    useShallow((state) => ({
      user: state.user,
    }))
  );

  const {
    data: userData,
    isLoading: isLoadingUser,
    isError: isErrorUser,
    error: userError,
  } = useModelById(user?.id);

  const {
    data: cities = [],
    isError: isErrorCities,
    error: citiesError,
  } = useCities();

  const {
    data: allServices = [],
    isError: isErrorServices,
    error: servicesError,
  } = useServices();

  const {
    data: modelServices = [],
    isError: isErrorModelServices,
    error: modelServicesError,
  } = useModelServices();

  const {
    data: modelFilters = [],
    isError: isErrorModelFilters,
    error: modelFiltersError,
  } = useModelFilters();

  const {
    data: filters,
    isLoading: isLoadingFilters,
    isError: isErrorFilters,
    error: filtersError,
  } = useFiltersByGender(userData?.gender || 'WOMAN');

  const { updateDescription, isLoading: isSaving } =
    useUpdateModelDescription();

  // Load initial data
  useEffect(() => {
    if (!userData) return;

    setFormData({
      nameFantasy: userData.fantasyName || '',
      impactPhrase: userData.impactPhrase || '',
      description: userData.description || '',
      selectedCity: userData.cityId || '',
      district: userData.district || '',
    });
  }, [userData]);

  // Load initial model Filters
  useEffect(() => {
    if (modelFilters.length === 0) return;

    const filtersObj = {};
    modelFilters.forEach((f) => {
      filtersObj[f.filterId] = f.content;
    });
    setSelectedFilters(filtersObj);
  }, [modelFilters]);

  // Load initial model Services
  useEffect(() => {
    if (modelServices.length === 0) return;

    const servicesObj = {};
    modelServices.forEach((s) => {
      servicesObj[s.serviceId] = s.optionType;
    });
    setSelectedServices(servicesObj);
  }, [modelServices]);

  // Valid form
  const isFormValid = useMemo(() => {
    const { nameFantasy, selectedCity } = formData;
    return Boolean(nameFantasy && selectedCity);
  }, [formData]);

  // Handlers
  const updateFormField = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleFilterToggle = (filterId, value = null, isMultiple = false) => {
    setSelectedFilters((prev) => {
      const newFilters = { ...prev };

      // Filtro múltiplo - trabalha com arrays
      if (isMultiple && value) {
        const currentValues = Array.isArray(newFilters[filterId])
          ? newFilters[filterId]
          : [];

        if (currentValues.includes(value)) {
          // Remove o valor se já existe
          const filtered = currentValues.filter((v) => v !== value);
          if (filtered.length === 0) {
            delete newFilters[filterId];
          } else {
            newFilters[filterId] = filtered;
          }
        } else {
          // Adiciona o novo valor
          newFilters[filterId] = [...currentValues, value];
        }
      }
      // Filtro com valor único (não múltiplo)
      else if (value) {
        if (newFilters[filterId] === value) {
          delete newFilters[filterId];
        } else {
          newFilters[filterId] = value;
        }
      }
      // Filtro booleano
      else {
        if (newFilters[filterId] === true) {
          delete newFilters[filterId];
        } else {
          newFilters[filterId] = true;
        }
      }

      return newFilters;
    });
  };

  const handleServiceChange = (serviceId, value) => {
    setSelectedServices((prev) => ({
      ...prev,
      [serviceId]: value,
    }));
  };

  const handleServiceRemove = (serviceId) => {
    setSelectedServices((prev) => {
      const newServices = { ...prev };
      delete newServices[serviceId];
      return newServices;
    });
  };

  // Send function
  const onSubmit = async () => {
    if (!isFormValid) {
      toast.error('Preencha o nome fantasia e a cidade');
      return;
    }

    try {
      const descriptionData = {
        fantasyName: formData.nameFantasy,
        description: formData.description,
        impactPhrase: formData.impactPhrase,
        cityId: formData.selectedCity,
        district: formData.district,
      };

      const servicesToSave = Object.entries(selectedServices)
        .filter(([, optionType]) => optionType && optionType.trim() !== '')
        .map(([serviceId, optionType]) => ({ serviceId, optionType }));

      await updateDescription({
        descriptionData,
        filters: selectedFilters,
        services: servicesToSave,
      });

      toast.success('Dados atualizados com sucesso!');
      navigate('/modelvalue');
    } catch (error) {
      console.log(error);
      toast.error('Erro ao atualizar dados. Tente novamente.');
    }
  };

  // ✅ Verificar erros
  const isError =
    isErrorUser ||
    isErrorCities ||
    isErrorServices ||
    isErrorModelServices ||
    isErrorModelFilters ||
    isErrorFilters;

  const errorMessage =
    userError?.message ||
    citiesError?.message ||
    servicesError?.message ||
    modelServicesError?.message ||
    modelFiltersError?.message ||
    filtersError?.message;

  if (isLoadingUser) {
    return <Loader isLoading />;
  }

  if (isError) {
    return <Error isError message={errorMessage} />;
  }

  return (
    <>
      {isSaving && <Loader isLoading />}

      <FormContainer>
        <Center className="center">
          <styles.Container>
            {/* Conteúdo das Seções */}

            <BasicInfoSection
              formData={formData}
              updateFormField={updateFormField}
              cities={cities}
            />

            <FiltersSection
              filters={filters}
              selectedFilters={selectedFilters}
              onFilterToggle={handleFilterToggle}
              isLoading={isLoadingFilters}
            />

            <ServicesSection
              services={allServices}
              selectedServices={selectedServices}
              onServiceChange={handleServiceChange}
              onServiceRemove={handleServiceRemove}
            />

            <styles.Button
              type="button"
              onClick={onSubmit}
              disabled={isSaving || !isFormValid}
            >
              <Text.P $upper>
                {isSaving ? 'Salvando...' : 'CONFIRMO MINHAS INFORMAÇÕES'}
              </Text.P>
            </styles.Button>
          </styles.Container>
        </Center>
      </FormContainer>
    </>
  );
}
